import React, { ChangeEvent, useCallback, useState } from "react";
import { AbbreviatedItem, MaintenanceMethod, NumericRange, SupplierSummary } from "../../types/item_types";
import InfoSectionMenu from "./InfoSectionMenu";
import LabeledInput from "../UI/LabeledInput";
import UploadFile from "../UI/UploadFile";
import { backendFirebaseUri, fetchBackend } from "../../backend-variables/address";
import { useAppSelector } from "../../hooks/redux-hooks";
import DebouncingInput from "../UI/DebouncingInput";
import classes from './ItemMenu.module.css';
import { MdEdit } from "react-icons/md";
import { default as SupplierListItem } from "../supplier-page/ListItem"
import MaintenanceMethodSelection from "./MaintenanceMethodSelection";

interface RangeInputProps {
    value?: NumericRange | null;
    onChange?: (value: NumericRange | null) => void;
    minLimit?: number;
    maxLimit?: number;
    step?: string | number;
    minPlaceholder?: string;
    maxPlaceholder?: string;
    unitSymbol?: string;
}

const RangeInput: React.FC<RangeInputProps> = ({
    value,
    onChange,
    minLimit,
    maxLimit,
    step,
    minPlaceholder = "מ-",
    maxPlaceholder = "עד-",
    unitSymbol
}) => {
    const handleMinChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value !== "" ? Number(e.target.value) : null;
        const newRange = { min: val, max: value?.max ?? null };
        onChange?.(newRange.min === null && newRange.max === null ? null : newRange);
    };

    const handleMaxChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value !== "" ? Number(e.target.value) : null;
        const newRange = { min: value?.min ?? null, max: val };
        onChange?.(newRange.min === null && newRange.max === null ? null : newRange);
    };

    // Dynamic limits:
    // - The min input cannot exceed the current max value (or maxLimit fallback)
    // - The max input cannot go lower than the current min value (or minLimit fallback)
    const minInputMax = value?.max ?? maxLimit;
    const maxInputMin = value?.min ?? minLimit;

    return (
        <div className={classes.rangeInputContainer}>
            {unitSymbol && <span className={classes.unitSymbol}>{unitSymbol}</span>}
            <input
                type="number"
                placeholder={minPlaceholder}
                min={minLimit}
                max={minInputMax}
                step={step}
                value={value?.min ?? ""}
                onChange={handleMinChange}
            />
            <span className={classes.rangeSeparator}>-</span>
            <input
                type="number"
                placeholder={maxPlaceholder}
                min={maxInputMin}
                max={maxLimit}
                step={step}
                value={value?.max ?? ""}
                onChange={handleMaxChange}
            />
        </div>
    );
};

interface DeviceFieldsProps {
    cat: string;
    imageLink: string;
    isImageUploading?: boolean;
    qaStandardLink: string;
    isQaStandardUploading?: boolean;
    userManualLink: string;
    isUserManualUploading?: boolean;
    medicalEngineeringManualLink: string;
    isMedicalEngineeringManualUploading?: boolean;
    serviceManualLink: string;
    isServiceManualUploading?: boolean;
    hebrewManualLink: string;
    schemasLink: string;
    isHebrewManualUploading?: boolean;
    isSchemasUploading?: boolean;
    supplier: SupplierSummary | null | undefined;
    price?: number | null;
    maintenanceMethod: MaintenanceMethod,
    maintenanceMethodsToChooseFrom?: MaintenanceMethod[];
    maintenanceIntervalMonths?: number | null;    
    storageTemperature?: NumericRange | null;
    storageHumidity?: NumericRange | null;
    minimumStock?: number | null;
    models: AbbreviatedItem[];
    accessories: AbbreviatedItem[];
    consumables: AbbreviatedItem[];
    spareParts: AbbreviatedItem[];
    handleInput: (setFunc: ((value: string) => void) | undefined, event: ChangeEvent<HTMLInputElement>) => void;
    setImageLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setQaStandardLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setMedicalEngineeringManualLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setUserManualLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setServiceManualLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setHebrewManualLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setSchemasLink?: React.Dispatch<React.SetStateAction<string | File>>;
    setSupplier?: React.Dispatch<React.SetStateAction<SupplierSummary | null | undefined>>;
    handleSetPrice?: (value: number | null) => void;
    handleSetMaintenanceMethod?: (maintenanceMethod: MaintenanceMethod) => void;
    handleSetMaintenanceIntervalMonths?: (value: number | null) => void;    
    handleSetStorageTemperature?: (value: NumericRange | null) => void;
    handleSetStorageHumidity?: (value: NumericRange | null) => void;
    handleMinimumStock?: (value: number | null) => void;    
    setModels?: React.Dispatch<React.SetStateAction<AbbreviatedItem[]>>;
    setAccessories?: React.Dispatch<React.SetStateAction<AbbreviatedItem[]>>;
    setConsumables?: React.Dispatch<React.SetStateAction<AbbreviatedItem[]>>;
    setSpareParts?: React.Dispatch<React.SetStateAction<AbbreviatedItem[]>>;
    fields?: string[];
    elementWrapper?: (element: JSX.Element, field: string) => JSX.Element;    
}

const DeviceFields = (props: DeviceFieldsProps) => {
    const {
        cat, imageLink, isImageUploading, qaStandardLink, isQaStandardUploading, medicalEngineeringManualLink, isMedicalEngineeringManualUploading, userManualLink, isUserManualUploading, serviceManualLink, isServiceManualUploading,
        hebrewManualLink, isHebrewManualUploading, schemasLink, isSchemasUploading, supplier, price, maintenanceMethod, maintenanceMethodsToChooseFrom, maintenanceIntervalMonths, storageTemperature, storageHumidity, minimumStock, models, accessories, consumables, spareParts, handleInput, setImageLink,
        setQaStandardLink, setMedicalEngineeringManualLink, setUserManualLink, setServiceManualLink, setHebrewManualLink, setSchemasLink, setSupplier, handleSetPrice,
        handleSetMaintenanceMethod, handleSetMaintenanceIntervalMonths, handleSetStorageTemperature, handleSetStorageHumidity, handleMinimumStock, setModels, setAccessories, setConsumables, setSpareParts, fields, elementWrapper
    } = props;

    const authToken = useAppSelector(state => state.auth.jwt);
    const [ itemSuggestions, setItemSuggestions ] = useState([]);

    const [ supplierSuggestions, setSupplierSuggestions ] = useState([]);
    const [ supplierSearchText, setSupplierSearchText ] = useState("");
    const [ showSupplierInput, setShowSupplierInput] = useState(false);

    const showSupplierListItem = !showSupplierInput && supplier;
    
    const fetchSupplier = useCallback(async (supplierId: string) => {
        const res = await fetchBackend(`suppliers/${supplierId}`, {
            headers: {
                'Content-Type': 'application/json',
                'auth-token': authToken
            }
        });
        const supplierDetails = await res.json();
        setSupplier?.(supplierDetails);
    }, [ authToken, setSupplier ]);    

    const getAutomaticCat = (collection: AbbreviatedItem[], collectionChar: string): string => {
        let i = 1;
        let autoCat: string;
        const catExists = (cat: string) => collection.some(item => item.cat === cat);
        do {
            autoCat = `${cat}-${collectionChar}${i++}`;
        }
        while (catExists(autoCat));
        return autoCat;
    }

    const namedElements = [
        { name: 'imageLink', element:
            <LabeledInput type="file" label="קישור לתמונה" value={imageLink} placeholder="קישור לתמונה" 
                customInputElement={<UploadFile placeholder= "קישור לתמונה" url={imageLink} accept="image/png, image/jpeg" isUploading={isImageUploading} onChange={(e) => setImageLink?.(e.target.files?.[0] ?? '')} onClear={() => setImageLink?.("")}/>}/>},
        { name: 'userManualLink', element: <LabeledInput type="file" label="מדריך למשתמש" value={userManualLink} placeholder="מדריך למשתמש"
            customInputElement={<UploadFile placeholder="מדריך למשתמש" url={userManualLink} isUploading={isUserManualUploading} onChange={(e) => setUserManualLink?.(e.target.files?.[0] ?? '')} onClear={() => setUserManualLink?.("")}/>}/>},
        { name: 'hebrewManualLink', element: <LabeledInput type="file" label="הוראות הפעלה בעברית" value={hebrewManualLink} placeholder="הוראות הפעלה בעברית" 
            customInputElement={<UploadFile placeholder="הוראות הפעלה בעברית" url={hebrewManualLink} isUploading={isHebrewManualUploading} onChange={(e) => setHebrewManualLink?.(e.target.files?.[0] ?? '')} onClear={() => setHebrewManualLink?.("")}/>}/>},
        { name: 'schemasLink', element: <LabeledInput type="file" label="סכמות/שרטוטים" value={schemasLink} placeholder="סכמות/שרטוטים"
            customInputElement={<UploadFile placeholder="סכמות/שרטוטים" url={schemasLink} isUploading={isSchemasUploading} onChange={(e) => setSchemasLink?.(e.target.files?.[0] ?? '')} onClear={() => setSchemasLink?.("")}/>}/>},
        { name: 'medicalEngineeringManualLink', element: <LabeledInput type="file" label="הוראות הנר" value={medicalEngineeringManualLink} placeholder="הוראות הנר" 
            customInputElement={<UploadFile placeholder="הוראות הנר" url={medicalEngineeringManualLink} isUploading={isMedicalEngineeringManualUploading} onChange={(e) => setMedicalEngineeringManualLink?.(e.target.files?.[0] ?? '')} onClear={() => setMedicalEngineeringManualLink?.("")}/>}/>},
        { name: 'qaStandardLink', element: <LabeledInput type="file" label="תקן בחינה" value={qaStandardLink} placeholder="תקן בחינה"
            customInputElement={<UploadFile placeholder="תקן בחינה" url={qaStandardLink} isUploading={isQaStandardUploading} onChange={(e) => setQaStandardLink?.(e.target.files?.[0] ?? '')} onClear={() => setQaStandardLink?.("")}/>}/>},
        { name: 'serviceManualLink', element: <LabeledInput type="file" label="Service Manual" value={serviceManualLink} placeholder="Service Manual" 
            customInputElement={<UploadFile placeholder="Service Manual" url={serviceManualLink} isUploading={isServiceManualUploading} onChange={(e) => setServiceManualLink?.(e.target.files?.[0] ?? '')} onClear={() => setServiceManualLink?.("")}/>}/>},
        { name: 'supplier', element:            
            <div className={classes.inputGroup}>
                <label htmlFor="supplierSearch">ספק בארץ</label>                
                {showSupplierListItem ? (
                    <span className={classes.listItemContainer}>
                        <SupplierListItem
                            className={classes.supplierListItem}
                            textContentClassName={classes.itemTextContent}
                            _id={supplier?._id ?? ""}
                            supplier={supplier}
                            goToSupplierPage={() => setShowSupplierInput(true)}
                        />
                        <MdEdit
                            onClick={() => {
                                setShowSupplierInput(true);
                                setSupplierSearchText(supplier.name);
                            }}
                        />
                    </span>
                ) : (
                    <DebouncingInput
                        id="supplierSearch"
                        className={classes.itemCat}
                        inputValue={supplierSearchText}
                        onValueChanged={(val: any) => setSupplierSearchText(val)}
                        onValueErased={() => setSupplier?.(null)}
                        onSuggestionSelected={(s: any) => {
                            setSupplier?.(s);
                            fetchSupplier(s._id);
                            setShowSupplierInput(false)
                        }}
                        getSuggestionValue={s => s.name}
                        placeholder='חפש ספק (שם, מזהה במשרד הביטחון)'
                        suggestions={supplierSuggestions}
                        onFetchSuggestions={(value: string) => {
                            fetchBackend(encodeURI(`suppliers?search=${value}`), {
                                method: 'GET',
                                headers: {
                                    'auth-token': authToken
                                }
                            })
                            .then((res) => res.json())
                            .then(jsonRes => setSupplierSuggestions(jsonRes))
                            .catch((err) => console.log(`Error getting item suggestions: ${err}`));
                        }}
                        renderSuggestion={s => <span>{s.id} {s.name}</span>}
                        onClearSuggestions={() => { setSupplierSuggestions([]); }}
                        onBlur={() => {
                            if (!supplierSuggestions.find((s: any) => s.id === supplierSearchText || s.name === supplierSearchText)) {
                                setSupplierSearchText("");
                            }
                        }}
                    />
                )}
            </div>},
        { name: 'price', element:<LabeledInput
            type="number"
            label='מחיר'
            placeholder='מחיר'
            min={0}
            value={price ?? ''}
            onChange={(e) => handleInput(val => handleSetPrice?.(Number.parseInt(val) ? +val : null), e)}
        />},
        { name: 'maintenanceMethod', element: <LabeledInput
            label='שיטת אחזקה'
            placeholder='שיטת אחזקה'
            customInputElement={
                <MaintenanceMethodSelection maintenanceMethods={maintenanceMethodsToChooseFrom} selectMaintenanceMethod={handleSetMaintenanceMethod} currentMaintenanceMethod={maintenanceMethod} />
            }/>
        },
        { name: 'maintenanceIntervalMonths', element: maintenanceMethod === MaintenanceMethod.PeriodicTestAndCalibration ? <LabeledInput
            type="number"
            label='תדירות אחזקה בחודשים'
            placeholder='תדירות אחזקה בחודשים'
            min={0}
            value={maintenanceIntervalMonths ?? ''}
            onChange={(e) => handleInput(val => handleSetMaintenanceIntervalMonths?.(Number.parseInt(val) ? +val : null), e)}
        /> : undefined },
        { name: 'storageTemperature', element: <LabeledInput
            label='טמפרטורת אחסנה'
            customInputElement={
                <RangeInput
                    value={storageTemperature}
                    minLimit={-300}
                    maxLimit={300}
                    step='any'
                    minPlaceholder="מינימום"
                    maxPlaceholder="מקסימום"
                    unitSymbol="°C"
                    onChange={(val) => handleSetStorageTemperature?.(val)}
                />
            }
        /> },
        { name: 'storageHumidity', element: <LabeledInput
            label='לחות אחסנה'
            customInputElement={
                <RangeInput
                    value={storageHumidity}
                    minLimit={0}
                    maxLimit={100}
                    minPlaceholder="מינימום"
                    maxPlaceholder="מקסימום"
                    unitSymbol="%"
                    onChange={(val) => handleSetStorageHumidity?.(val)}
                />
            }
        /> },                  
        { name: 'minimumStock', element: <LabeledInput
            type="number"
            label='מלאי קו אדום'
            placeholder='מלאי קו אדום'
            min={0}
            value={minimumStock ?? ''}
            onChange={(e) => handleInput(val => handleMinimumStock?.(Number.parseInt(val) ? +val : null), e)}
        />},
        { name: 'models', element: <InfoSectionMenu title="דגמים" items={models} setItems={setModels} />},
        { name: 'accessories', element:
            <InfoSectionMenu 
                title="אביזרים"
                items={accessories}
                setItems={setAccessories}
                itemSuggestions={itemSuggestions}
                allowNewItem={true}
                onFetchSuggestions={(value: string, field: string) => {
                    return fetch(encodeURI(`${backendFirebaseUri}/items?catType=אביזר&search=${value}&searchFields=${field}`), {
                        method: 'GET',
                        headers: {
                            'auth-token': authToken
                        }
                    })
                    .then((res) => res.json())
                    .then(jsonRes => setItemSuggestions(jsonRes))
                    .catch((err) => console.log(`Error getting item suggestions: ${err}`));
                }}
                onClearSuggestions={() => setItemSuggestions([])}
                getAutomaticCat={() => getAutomaticCat(accessories, 'a')}
            />},
        { name: 'consumables', element:
            <InfoSectionMenu 
                title="מתכלים"
                items={consumables}
                setItems={setConsumables}
                itemSuggestions={itemSuggestions}
                allowNewItem={true}
                onFetchSuggestions={(value: string, field: string) => {
                    return fetch(encodeURI(`${backendFirebaseUri}/items?catType=מתכלה&search=${value}&searchFields=${field}`), {
                        method: 'GET',
                        headers: {
                            'auth-token': authToken
                        }
                    })
                    .then((res) => res.json())
                    .then(jsonRes => setItemSuggestions(jsonRes))
                    .catch((err) => console.log(`Error getting item suggestions: ${err}`));
                }}
                onClearSuggestions={() => setItemSuggestions([])}
                getAutomaticCat={() => getAutomaticCat(consumables, 'c')}
            />},
        { name: 'spareParts', element:
            <InfoSectionMenu 
                title="חלקי חילוף"
                items={spareParts}
                setItems={setSpareParts}
                itemSuggestions={itemSuggestions}
                allowNewItem={true}
                onFetchSuggestions={(value: string, field: string) => {
                    return fetch(encodeURI(`${backendFirebaseUri}/items?catType=חלק חילוף&search=${value}&searchFields=${field}`), {
                        method: 'GET',
                        headers: {
                            'auth-token': authToken
                        }
                    })
                    .then((res) => res.json())
                    .then(jsonRes => setItemSuggestions(jsonRes))
                    .catch((err) => console.log(`Error getting item suggestions: ${err}`));
                }}
                onClearSuggestions={() => setItemSuggestions([])}
                getAutomaticCat={() => getAutomaticCat(spareParts, 's')}
            />
        }
    ];

    return (
        <>
            {namedElements
                .filter(({ name, element }) => element && (!fields || fields.includes(name)))
                .map(({ element, name }) => elementWrapper ? elementWrapper(element!, name) : element)}
        </>
    );
}

export default DeviceFields;